import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  SignalVisibilityType,
  SignalApplicabilityScope,
  SEVERITY_LABELS,
} from '@momentum/types';
import { useGenerateSignalsFromFile } from '@/shared/hooks/useSignals';
import type {
  SignalDefaults,
  ReviewableSignal,
} from '../../signal-review/types';

type Step =
  | 'source'
  | 'upload'
  | 'loading'
  | 'defaults'
  | 'individual'
  | 'summary';

const INITIAL_DEFAULTS: SignalDefaults = {
  severity: SEVERITY_LABELS.Medium,
  visibility: SignalVisibilityType.PUBLIC,
  isAnonymous: false,
  applicabilityScope: SignalApplicabilityScope.SELF,
  viewableBy: { users: [], teams: [] },
};

interface UseImportFlowProps {
  open: boolean;
}

export function useImportFlow({ open }: UseImportFlowProps) {
  const { t } = useTranslation('knowledge');
  const [step, setStep] = useState<Step>('source');
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [files, setFiles] = useState<File[]>([]);
  const [defaults, setDefaults] = useState<SignalDefaults>(INITIAL_DEFAULTS);

  const generateFromFileMutation = useGenerateSignalsFromFile();

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('source');
      setFiles([]);
      setDefaults(INITIAL_DEFAULTS);
    }
  }, [open]);

  const handleFilesSelected = useCallback((selected: File[]) => {
    setFiles((prev) => [...prev, ...selected]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  }, []);

  const handleUpload = useCallback(
    async (initializeSignals: (suggestions: ReviewableSignal[]) => void) => {
      if (files.length === 0) return;

      setDirection('forward');
      setStep('loading');

      try {
        const result = await generateFromFileMutation.mutateAsync(files);
        const suggestions = result.suggestions ?? [];

        initializeSignals(
          suggestions.map((suggestion, suggestionIndex) => ({
            index: suggestionIndex,
            title: suggestion.title,
            context: suggestion.context,
            sourceContext: suggestion.sourceContext,
            sourceQuote: suggestion.sourceQuote,
            severity: suggestion.severity ?? defaults.severity,
            missability: suggestion.missability,
            visibility: suggestion.visibility ?? defaults.visibility,
            isAnonymous: suggestion.isAnonymous ?? defaults.isAnonymous,
            viewableBy: defaults.viewableBy,
            applicabilityScope: defaults.applicabilityScope,
            excluded: false,
            edited: false,
            fileName: suggestion.fileName,
            category: suggestion.category,
            informedBy: suggestion.informedBy,
            reasoning: suggestion.reasoning,
          }))
        );

        setStep('defaults');
      } catch {
        toast.error(t('importFileError'));
        setStep('upload');
      }
    },
    [files, generateFromFileMutation, defaults, t]
  );

  const handleBackToSource = useCallback(() => {
    setDirection('back');
    setStep('source');
  }, []);

  const handleSourceSelected = useCallback((_sourceId: string) => {
    setDirection('forward');
    setStep('upload');
  }, []);

  const goToStep = useCallback(
    (target: Step, dir: 'forward' | 'back' = 'forward') => {
      setDirection(dir);
      setStep(target);
    },
    []
  );

  return {
    step,
    direction,
    files,
    defaults,
    setDefaults,
    handleFilesSelected,
    handleRemoveFile,
    handleUpload,
    handleBackToSource,
    handleSourceSelected,
    goToStep,
  };
}
